import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-hot-toast';

interface CommunicateFixModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  problemId: string;
  problemNumber: string;
  contactId?: string | null;
  approvalGroupId?: string | null;
  engagementId?: string | null;
  isResend?: boolean;
}

const CommunicateFixModal: React.FC<CommunicateFixModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  problemId,
  problemNumber,
  contactId: _contactId,
  approvalGroupId: _approvalGroupId,
  engagementId: _engagementId,
  isResend = false
}) => {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const url = isResend
        ? `/cases/${problemId}/request-customer-approval?resend=true`
        : `/cases/${problemId}/request-customer-approval`;
      const response = await api.post(url);
      toast.success(response.data.message || (isResend ? 'Approval request resent successfully' : 'Approval request sent successfully'));
      onConfirm();
      handleClose();
    } catch (error: any) {
      console.error('Error requesting approval:', error);
      toast.error(error.response?.data?.message || 'Failed to send approval request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    {isResend ? 'Resend Customer Approval Request' : 'Communicate Fix - Request Customer Approval'}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <>
                    <div className="mt-4">
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                              <strong>{isResend ? 'Confirm Resend Approval Request' : 'Confirm Approval Request'}</strong>
                              <br />
                              This will {isResend ? 'resend the' : 'send an'} approval request for problem <strong>{problemNumber}</strong> to:
                            </p>
                            <ul className="list-disc list-inside text-sm text-yellow-700 mt-2 ml-2">
                              <li>Contact (if provided) or all customer contacts from the engagement</li>
                              <li>All members of customer approval groups</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">What happens next?</h4>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                          <li>Email notifications will be sent to all recipients</li>
                          <li>Recipients can approve or reject the proposed fix</li>
                          <li>When any recipient approves, the problem will move to Resolved state</li>
                          <li>If any recipient rejects, you will be notified to revise the fix</li>
                        </ul>
                      </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                        onClick={handleClose}
                        disabled={submitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        onClick={handleSubmit}
                        disabled={submitting}
                      >
                        {submitting ? 'Sending...' : (isResend ? 'Resend Approval Request' : 'Send Approval Request')}
                      </button>
                    </div>
                  </>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CommunicateFixModal;
